import { useEffect, useId, useRef } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import kitanegoLogoWordmark from "./assets/kitanego-logo-wordmark.png";
import type { InterfaceLanguage } from "./i18n";

export default function AboutUsDialog({ language, onClose }: { language: InterfaceLanguage; onClose: () => void }) {
  const titleId = useId();
  const closeButton = useRef<HTMLButtonElement>(null);
  const closeLabel = language === "en" ? "Close" : "Tutup";

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeButton.current?.focus();
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [onClose]);

  return createPortal(
    <div className="about-us-dialog" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <article className="about-us-dialog__window" role="dialog" aria-modal="true" aria-labelledby={titleId}>
        <header className="about-us-dialog__header">
          <div className="about-us-dialog__brand">
            <img src={kitanegoLogoWordmark} alt="Kitanego" />
          </div>
          <button ref={closeButton} type="button" className="about-us-dialog__close" onClick={onClose} aria-label={closeLabel}><X /></button>
        </header>

        <div className="about-us-dialog__content">
          <p className="about-us-dialog__lead" id={titleId}>About us</p>
          <p>Together, we can change the way the world buys. What if the power to change the marketplace has been in our hands all along, waiting for us to discover what we can achieve together?</p>
          <p>Every day, across the world, millions of people work hard to build better lives for themselves and their families. They give their time, their energy, and their best efforts to earn a living. Yet, when they enter the marketplace, they often stand alone. One person, one household, one purchase at a time.</p>
          <p>Individually, their choices may seem small, their voices easy to overlook. But what if we’ve been looking at purchasing power the wrong way? What if the real opportunity isn’t simply how much one person can buy, but what millions can achieve together?</p>
          <p>This belief gave birth to Kita Nego. We exist because we believe ordinary people deserve the opportunity to turn their individual needs into collective strength.</p>
          <p>At Kita Nego, we’re building more than a purchasing platform. We’re building a community where needs are heard, trust is earned, and possibilities are created through collective demand. We begin by listening, by understanding what people genuinely buy, and by bringing their shared needs into clearer focus.</p>
          <p>Our ambition reaches beyond borders because the chance to work together for better value isn’t limited by geography or nationality. Wherever people share everyday needs, there’s an opportunity to imagine something new.</p>
          <p>We envision a future where consumers are not just individuals responding to the marketplace, but a community with the confidence to shape better purchasing opportunities.</p>
          <p>We are not asking the world to believe that one person can change everything. We are inviting people to discover what becomes possible when we stop acting alone and start working together.</p>
          <p>Because lasting change does not always begin with the biggest corporations or the deepest pockets. Sometimes it begins when ordinary people recognize their shared strength and decide to use it.</p>
          <blockquote><strong>Kita Bersatu, Kita Runding, Kita Jimat.</strong><span>We Unite, We Negotiate, We save.</span></blockquote>
        </div>

      </article>
    </div>,
    document.body,
  );
}
